import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  GitBranch,
  Settings,
  GitCommit,
} from 'lucide-react';
import { BranchManager } from './BranchManager';
import { CompareView } from './CompareView';
import { DiffViewer } from './DiffViewer';
import { useGitStatus } from '@/hooks/useGitStatus';

interface AdvancedGitDialogProps {
  projectId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function AdvancedGitDialog({
  projectId,
  open,
  onOpenChange,
}: AdvancedGitDialogProps) {
  const [activeTab, setActiveTab] = useState('branches');
  const { data: gitStatus, refetch: refetchGitStatus } = useGitStatus(projectId);

  const handleBranchChange = () => {
    refetchGitStatus();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Settings className="h-4 w-4" />
          Advanced
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0 flex flex-col gap-0">
        {/* Fixed Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Git Management
          </DialogTitle>
        </DialogHeader>

        {/* Tabs Container */}
        <div className="flex-1 flex flex-col min-h-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            {/* Fixed Tab List */}
            <div className="border-b px-6 shrink-0">
              <TabsList className="h-auto p-0 bg-transparent">
                <TabsTrigger
                  value="branches"
                  className="gap-2 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                >
                  <GitBranch className="h-4 w-4" />
                  Branches
                </TabsTrigger>
                <TabsTrigger
                  value="changes"
                  className="gap-2 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                >
                  <Settings className="h-4 w-4" />
                  Changes
                </TabsTrigger>
                <TabsTrigger
                  value="compare"
                  className="gap-2 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                >
                  <GitCommit className="h-4 w-4" />
                  Compare
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Branches Tab */}
            <TabsContent value="branches" className="flex-1 m-0 data-[state=active]:flex data-[state=active]:flex-col min-h-0">
              <ScrollArea className="flex-1">
                <div className="p-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <GitBranch className="h-4 w-4" />
                        Branches
                      </CardTitle>
                      <CardDescription>Manage branches and switch between them</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <BranchManager
                        projectId={projectId}
                        currentBranch={gitStatus?.currentBranch || null}
                        onBranchChange={handleBranchChange}
                      />
                    </CardContent>
                  </Card>
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Changes Tab */}
            <TabsContent value="changes" className="flex-1 m-0 data-[state=active]:flex data-[state=active]:flex-col min-h-0">
              <ScrollArea className="flex-1">
                <div className="p-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <GitCommit className="h-4 w-4" />
                        Working Directory
                      </CardTitle>
                      <CardDescription>View uncommitted changes in your working directory</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      <DiffViewer
                        projectId={projectId}
                        compareFrom="HEAD"
                      />
                    </CardContent>
                  </Card>
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Compare Tab */}
            <TabsContent value="compare" className="flex-1 m-0 data-[state=active]:flex data-[state=active]:flex-col min-h-0">
              <ScrollArea className="flex-1">
                <div className="p-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <GitCommit className="h-4 w-4" />
                        Compare Commits
                      </CardTitle>
                      <CardDescription>Compare changes between branches or commits</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <CompareView projectId={projectId} />
                    </CardContent>
                  </Card>
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
